import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { ChevronsRight, CircleCheck, Eye, EyeOff } from 'lucide-react';
import { PRODUCT_BRAND_NAME } from '@erp/shared';
import type { LoginBody, LoginResponse } from '@erp/shared';
import { api, type ApiError } from '@/lib/api-client';
import { isAuthenticated, loginSession } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Arte do painel esquerdo (referência: login do Tiflux): dois chevrons-"ticket"
 * entrelaçados em traço fino — petróleo atrás, laranja na frente — com os
 * recortes de meia-lua de bilhete nas bordas.
 */
function TicketChevronArt({ className }: { className?: string }) {
  // Banda em ">" (fita de ticket): topo curto → diagonal externa até a ponta →
  // diagonal externa de volta → base curta → diagonais internas. Recortes de
  // meia-lua (bite de bilhete) no meio das arestas horizontais.
  const chevron = (dx: number, dy: number) =>
    `M ${dx} ${dy} h 42 a 16 16 0 0 0 32 0 h 42 L ${dx + 356} ${dy + 300} L ${dx + 116} ${dy + 600} h -42 a 16 16 0 0 0 -32 0 h -42 L ${dx + 240} ${dy + 300} Z`;
  return (
    <svg
      viewBox="0 0 740 1000"
      fill="none"
      aria-hidden
      className={cn('pointer-events-none select-none', className)}
    >
      {/* chevron petróleo (trás, acima-esquerda) */}
      <path d={chevron(20, 40)} stroke="#03363D" strokeWidth="4" strokeLinejoin="round" />
      {/* chevron laranja (frente), entrelaçado a partir da ponta do petróleo */}
      <path d={chevron(258, 340)} stroke="#F26522" strokeWidth="4" strokeLinejoin="round" />
      {/* ponta extra fina do laranja, como no original */}
      <path
        d="M494 380 L716 640 L494 900"
        stroke="#F26522"
        strokeWidth="4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Marca do PRODUTO: o login é antes de haver tenant (spec 017) — o nome do
 * primeiro cliente escrito aqui era o que um segundo cliente veria ao entrar.
 */
function Wordmark({ className, iconClassName }: { className?: string; iconClassName?: string }) {
  return (
    <span className={cn('flex select-none items-center font-bold tracking-tight', className)}>
      {PRODUCT_BRAND_NAME}
      <ChevronsRight className={cn('-ml-0.5 text-[#F26522]', iconClassName)} strokeWidth={3} />
    </span>
  );
}

function ValidCheck({ show }: { show: boolean }) {
  if (!show) return null;
  return <CircleCheck className="size-4 fill-[#52C41A] text-white" aria-hidden />;
}

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5 3.7-8.6z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.1-6.9-5.1L1.3 17C3.3 21.2 7.3 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.1 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.7C.5 8.3 0 10.1 0 12s.5 3.7 1.3 5.3l3.8-3z"
      />
      <path
        fill="#EA4335"
        d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.7l3.8 3c.9-3 3.7-5 6.9-5z"
      />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) void navigate({ to: '/' });
  }, [navigate]);

  const login = useMutation<LoginResponse, ApiError, LoginBody>({
    mutationFn: async (body) => {
      const res = await api<LoginResponse>('/auth/login', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      loginSession(data.token, data.user);
      void navigate({ to: '/' });
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    login.mutate({ email, password });
  }

  const errorMessage = login.error
    ? login.error.status === 401 || login.error.error === 'invalid_credentials'
      ? 'E-mail ou senha inválidos'
      : 'Não foi possível entrar. Tente novamente.'
    : null;

  return (
    <div className="flex min-h-dvh items-center bg-background">
      {/* ── Arte (canvas claro, como no original) ──────────────────────────── */}
      <div className="relative hidden h-dvh w-1/2 items-center justify-center lg:flex">
        <TicketChevronArt className="h-[70%] w-auto" />
      </div>

      {/* ── Card do formulário ─────────────────────────────────────────────── */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-[460px] rounded-xl bg-card p-9 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
          <Wordmark className="text-[26px] text-sidebar" iconClassName="size-6" />
          <p className="mt-5 text-base font-medium text-foreground">Acessar conta</p>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="login-email" className="text-sm font-semibold">
                E-mail<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 pr-9"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  <ValidCheck show={EMAIL_RE.test(email)} />
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-end justify-between">
                <Label htmlFor="login-password" className="text-sm font-semibold">
                  Senha<span className="ml-0.5 text-destructive">*</span>
                </Label>
                <button
                  type="button"
                  title="Fale com um administrador para redefinir a senha"
                  className="cursor-help text-xs font-medium text-foreground underline-offset-2 hover:underline"
                >
                  Esqueci minha senha
                </button>
              </div>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-10 pr-16"
                />
                <span className="absolute right-0 top-0 flex h-10 items-center gap-1.5 pr-3">
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    className="flex items-center text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                  <ValidCheck show={password.length >= 6} />
                </span>
              </div>
            </div>

            <label className="flex w-fit cursor-pointer items-center gap-2 text-sm text-foreground">
              <Checkbox defaultChecked aria-label="Mantenha-me conectado" />
              Mantenha-me conectado
            </label>

            {errorMessage ? (
              <p role="alert" className="text-sm text-destructive">
                {errorMessage}
              </p>
            ) : null}

            <Button
              type="submit"
              disabled={login.isPending}
              className="h-11 w-full text-[15px] font-semibold hover:bg-primary-hover active:bg-primary-pressed"
            >
              {login.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
              Entrar
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <span className="text-sm text-muted-foreground">ou acessar com</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button variant="outline" type="button" disabled title="Em breve" className="h-10">
              <GoogleLogo />
              Google
            </Button>
            <Button variant="outline" type="button" disabled title="Em breve" className="h-10">
              <MicrosoftLogo />
              Microsoft
            </Button>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Acesso da equipe ·{' '}
            <span className="underline underline-offset-2">Fale com um administrador</span>
          </p>
        </div>
      </div>
    </div>
  );
}
